import { ToDo } from "../../Components/ToDo";
import { Step } from "../Step/Step";
import { Cash } from "../Step/Cash";
import { StepEvent, StepEventType } from "../Step/StepEvent";
import { StepType } from "../Step/StepType";
import { PaymentStepData } from "./Models/PaymentStepData";
import { PaymentInfoForCustomer } from "./Components/PaymentInfoForCustomer";
import { PaymentInfoForAdmin } from "./Components/PaymentInfoForAdmin";
import { PaymentStepState } from "./Models/PaymentStepState";
import { MultilineInput } from "@tblabs/truffle/Controls/Inputs/MultilineInput";
import { Line } from "@tblabs/truffle/Controls/Elements/Line";
import { DateTimeProvider } from "../../Services/DateTimeProvider";
import { PaymentFormNames } from "../../Models/Payment/PaymentForm";
import { Human } from "../../Models/Human";
import { CashType } from "../Step/CashType";


export class PaymentStep extends Step<PaymentStepState>
{
    public Type = StepType.Payment;
    public Icon = "💳";
    public TitleForCustomer = () => `Płatność`;
    public Title = () => `Płatność (${PaymentFormNames[this.Data.Form.value]}, ${this.Data.Amount.value} ${this.Data.Currency.value})`;

    public InitialState = PaymentStepState.Awaiting;
    public WellDoneStates = [PaymentStepState.Paid, PaymentStepState.Omitted];
    public FailedDoneStates = [PaymentStepState.Canceled];

    public get AsEvents(): StepEvent[]
    {
        return [
            new StepEvent(StepEventType.Payment, this.Order.Id, "💰", "Płatność", this.Data.Deadline.value, this.IsDone)
        ];
    }

    public get Mana(): Cash[]
    {
        const cash: Cash[] = [];

        if (this.Data.State.Is(PaymentStepState.Paid))
        {
            let income = this.Data.Amount.value - this.Order.DesiredDeposit;
            if (income < 0)
                income = 0;
            cash.push(new Cash(CashType.ReceivedIncome, this, income, "Wpłacone środki", new Date(+this.Data.WhenPaid.value || +this.Data.Deadline.value)))
            cash.push(new Cash(CashType.ReceivedDeposit, this, this.Order.DesiredDeposit, "Wpłacona kaucja", new Date(+this.Data.WhenPaid.value || +this.Data.Deadline.value)))
        }

        return cash;
    }

    public get WhenPlanned(): Date
    {
        return this.Data.Deadline.value;
    }

    public get WhenDone(): Date
    {
        return this.Data.WhenPaid.value;
    }

    constructor(public Data: PaymentStepData)
    {
        super(Data);
    }

    public STATES = {
        [PaymentStepState.Awaiting]: {
            StateName: "Oczekuje na płatność",
            Timeout: () => this.Data.Deadline,
            Customer: {
                Content: () => [
                    `Szczegóły płatności:\n`,
                    new PaymentInfoForCustomer(this.Data)
                ],
            },
            Admin: {
                Content: () => [
                    `Oczekiwanie na wpłatę`,
                    new PaymentInfoForAdmin(this.Data, this.Order.People.First() || Human.Default),
                    new ToDo(`Sprawdź stan zaksięgowana: czy wpłata dotarła na czas, z właściwym kodem oraz czy kwota się zgadza`)
                ],
                Actions: {
                    "✅": () => { this.Data.WhenPaid.value = DateTimeProvider.Now; this.GoTo(PaymentStepState.Paid); },
                    "❌": () => this.GoTo(PaymentStepState.Canceled),
                },
            },
        },
        [PaymentStepState.Paid]: {
            StateName: "Opłacone",
            Customer: {
                Content: () => [`✅ Zamówienie zostało opłacone`],
            },
            Admin: {
                Content: () => [
                    `✅ Zamówienie zostało opłacone`,
                    new PaymentInfoForAdmin(this.Data, this.Order.People.First() || Human.Default),
                ],
            },
        },
        [PaymentStepState.Canceled]: {
            StateName: "Odwołane",
            Customer: {
                Content: () => [`❌ Możliwość przyjęcia tej płatności została anulowana`],
            },
            Admin: {
                Content: () => [`❌ Płatność anulowana`,
                    new ToDo(
                        `Jeśli płatność została tylko pominięta (np. z tytułu opłacenia w innym zamówieniu) to kliknij X poniżej`,
                        new MultilineInput(this.Data.CancellationReason).Placeholder("Powód pominięcia płatności. Widoczne również dla klienta!").Rows(3)
                    )],
                Actions: {
                    "✖️": () => this.GoTo(PaymentStepState.Omitted),
                },
            },
        },
        [PaymentStepState.Omitted]: {
            StateName: "Pominięte",
            Customer: {
                Content: () => [
                    `Płatność nie jest konieczna`,
                    new Line(),
                    this.Data.CancellationReason.value],
            },
            Admin: {
                Content: () => [
                    `Płatność została pominięta`,
                    new Line(),
                    this.Data.CancellationReason.value],
            },
        },
        [PaymentStepState.Error]: {
            StateName: "Błąd płatności",
            Customer: {
                Content: () => [
                    `Błąd podczas wykonywania operacji.`],
            },
            Admin: {
                Content: () => [
                    `Wystąpił błąd podczas wykonywania operacji. Sytuacja wymaga wyjaśnienia!`],
            },
        },
        [PaymentStepState.Returned]: {
            StateName: "Zwrócone",
            Customer: {
                Content: () => [
                    `Płatność została anulowana przez bank. Trwa wyjaśnianie sytuacji...`],
            },
            Admin: {
                Content: () => [
                    `Płatność została anulowana przez bank. Sytuacja wymaga wyjaśnienia!`,
                ],
            },
        },
    };
}
